import { useCallback, useEffect, useRef, useState } from 'react'

export const DRAG_THRESHOLD_PX = 4

// Owns per-node positions and the drag gesture: pointer-down arms a drag, a
// window pointer-move past the threshold moves the node (screen delta ÷ current
// zoom, clamped to the world origin), and pointer-up flags a "this was a drag"
// so the click that fires next doesn't re-select the node. `zoomRef` comes from
// useCanvasPanZoom so deltas convert to world space.
export const useNodeDrag = ({ zoomRef }) => {
  const [nodePositions, setNodePositions] = useState({})

  const nodePositionsRef = useRef(nodePositions)
  const defaultPositionsRef = useRef({})
  const draggingRef = useRef(null)
  // Survives draggingRef cleanup so the click handler (fired AFTER pointerup
  // nulls draggingRef) can still detect that the prior gesture was a drag.
  const suppressNextClickRef = useRef(false)

  // Mirror positions in a ref so the pointer-move handler always sees the
  // latest values without re-binding the window listener.
  useEffect(() => {
    nodePositionsRef.current = nodePositions
  }, [nodePositions])

  useEffect(() => {
    const handlePointerMove = (event) => {
      const drag = draggingRef.current
      if (!drag || event.pointerId !== drag.pointerId) {
        return
      }
      const dxScreen = event.clientX - drag.startX
      const dyScreen = event.clientY - drag.startY
      if (!drag.moved) {
        if (Math.hypot(dxScreen, dyScreen) < DRAG_THRESHOLD_PX) {
          return
        }
        drag.moved = true
      }
      const dx = dxScreen / zoomRef.current
      const dy = dyScreen / zoomRef.current
      setNodePositions((prev) => ({
        ...prev,
        [drag.nodeId]: {
          x: Math.max(0, drag.startPos.x + dx),
          y: Math.max(0, drag.startPos.y + dy),
        },
      }))
    }

    const handlePointerUp = () => {
      const drag = draggingRef.current
      if (drag) {
        if (drag.moved) {
          suppressNextClickRef.current = true
        }
        draggingRef.current = null
      }
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [zoomRef])

  // Stable (CanvasNode is React.memo) — arms a drag from the node's current
  // position, capturing the pointer so moves keep targeting it.
  const handleNodePointerDown = useCallback((event, nodeId) => {
    if (event.button !== 0) {
      return
    }
    event.stopPropagation()
    const startPos = nodePositionsRef.current[nodeId]
    if (!startPos) {
      return
    }
    draggingRef.current = {
      nodeId,
      startX: event.clientX,
      startY: event.clientY,
      startPos,
      moved: false,
      pointerId: event.pointerId,
    }
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch (captureError) {
      // ignore — capture is a nicety, not required for the window listeners
    }
  }, [])

  // Returns true (once) when the last gesture was a drag, so the caller can
  // suppress the click that would otherwise re-select the node. Stable so the
  // caller's click handler can stay memo-safe.
  const consumeClickSuppression = useCallback(() => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false
      return true
    }
    return false
  }, [])

  const snapshotDefaults = (positions) => {
    defaultPositionsRef.current = positions
  }

  const resetToDefaults = () => {
    const defaults = { ...defaultPositionsRef.current }
    setNodePositions(defaults)
    return defaults
  }

  return {
    nodePositions,
    setNodePositions,
    handleNodePointerDown,
    consumeClickSuppression,
    snapshotDefaults,
    resetToDefaults,
  }
}
